import { CognitoUser, CognitoUserPool } from "amazon-cognito-identity-js";

import { getUserPool, isCognitoConfigured } from "./cognito-config";
import logger from "../utils/logger";

/**
 * Interface for forgot password operation result
 */
export interface ForgotPasswordResult {
  success: boolean;
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Interface for confirm password operation result
 */
export interface ConfirmPasswordResult {
  success: boolean;
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Initiate forgot password flow with Cognito
 * Sends a verification code to the user's email
 *
 * @param email - User's email address
 * @returns Promise resolving to ForgotPasswordResult
 *
 * @example
 * ```typescript
 * const result = await initiateForgotPassword('user@example.com');
 * if (result.success) {
 *   // Show UI to enter verification code and new password
 * } else {
 *   // Show error message
 * }
 * ```
 */
export function initiateForgotPassword(
  email: string,
): Promise<ForgotPasswordResult> {
  return new Promise((resolve) => {
    const userPool = getUserPool();

    if (!userPool) {
      resolve({
        success: false,
        error: {
          code: "ConfigurationError",
          message: "Cognito is not configured",
        },
      });
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve({ success: true });
      },
      onFailure: (err: any) => {
        logger.error("Forgot password initiation failed", {
          error: err?.message || "Unknown error",
          code: err?.code || err?.name,
        });
        resolve({
          success: false,
          error: {
            code: err.code || err.name || "ForgotPasswordFailed",
            message: err.message || "Failed to initiate password reset",
          },
        });
      },
    });
  });
}

/**
 * Confirm new password with verification code from email
 *
 * @param email - User's email address
 * @param verificationCode - Code received via email
 * @param newPassword - New password to set
 * @returns Promise resolving to ConfirmPasswordResult
 *
 * @example
 * ```typescript
 * const result = await confirmNewPassword(
 *   'user@example.com',
 *   '123456',
 *   'NewSecurePassword123!'
 * );
 * if (result.success) {
 *   // Password reset successful - redirect to login
 * } else {
 *   // Show error message
 * }
 * ```
 */
export function confirmNewPassword(
  email: string,
  verificationCode: string,
  newPassword: string,
): Promise<ConfirmPasswordResult> {
  return new Promise((resolve) => {
    const userPool = getUserPool();

    if (!userPool) {
      resolve({
        success: false,
        error: {
          code: "ConfigurationError",
          message: "Cognito is not configured",
        },
      });
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => {
        resolve({ success: true });
      },
      onFailure: (err: any) => {
        logger.error("Password confirmation failed", {
          error: err?.message || "Unknown error",
          code: err?.code || err?.name,
        });

        let errorMessage = err.message || "Failed to reset password";

        // Provide more user-friendly error messages
        if (err.code === "CodeMismatchException") {
          errorMessage =
            "Invalid verification code. Please check and try again.";
        } else if (err.code === "ExpiredCodeException") {
          errorMessage =
            "Verification code has expired. Please request a new one.";
        } else if (err.code === "InvalidPasswordException") {
          errorMessage =
            "Password does not meet requirements. Please try a stronger password.";
        } else if (err.code === "LimitExceededException") {
          errorMessage = "Too many attempts. Please try again later.";
        }

        resolve({
          success: false,
          error: {
            code: err.code || err.name || "ConfirmPasswordFailed",
            message: errorMessage,
          },
        });
      },
    });
  });
}

/**
 * Check if Cognito password reset is configured and available
 * @returns true if Cognito is properly configured, false otherwise
 */
export function isCognitoPasswordResetConfigured(): boolean {
  return isCognitoConfigured();
}
